/**
 * Leseprobe anfordern.
 *
 * Zwei Dinge passieren beim Absenden:
 *
 * 1. Ein Ereignis „Leseprobe angefordert" an Klaviyo, mit Buchtitel,
 *    Datei-URL und Produktlink. Darauf hört der Flow, der die Leseprobe
 *    verschickt. Das ist eine angeforderte Zustellung, keine Werbung, und
 *    läuft deshalb unabhängig von der Newsletter-Einwilligung.
 *
 * 2. Die Anmeldung zum Newsletter. Die Leseprobe ist daran gekoppelt: wer
 *    sie anfordert, meldet sich an. Deshalb steht der Einwilligungssatz
 *    sichtbar am Formular und nicht im Kleingedruckten.
 *
 *    Die Liste läuft auf **Single Opt-in**, der Eintrag gilt also sofort.
 *    Weil damit keine Bestätigungsmail den Nachweis führt, schreiben wir
 *    ihn selbst ans Profil: Zeitpunkt, Seite, Buch und der **Wortlaut**,
 *    dem zugestimmt wurde. Ändert sich der Satz im Theme, ändert sich der
 *    protokollierte Wortlaut mit. Das ist der Beleg, den § 7 Abs. 2 UWG
 *    im Streitfall verlangt.
 *
 *    Scheitert dieser zweite Aufruf, ist das kein Grund, die Leseprobe zu
 *    verweigern. Sie ist bereits unterwegs, der Fehler wird nur geloggt.
 *
 * Beides geht über Klaviyos öffentliche Client-Schnittstelle. Die braucht
 * nur die öffentliche Konto-ID, kein Geheimnis steht im Quelltext.
 *
 * ## Das Popup
 *
 * Dieselbe Mechanik, andere Hülle. Es hält sich an drei Regeln:
 *
 * - Es öffnet nicht, wenn das Formular auf derselben Seite schon fest
 *   steht. Auf Produkt- und Kollektionsseite ist es also still, dort
 *   öffnet ohnehin der Taktik-Berater.
 * - Wer es wegklickt, sieht es eine eingestellte Zahl von Tagen nicht.
 * - Wer die Leseprobe angefordert hat, sieht es ein Jahr nicht.
 *
 * Alle drei Merker liegen in `localStorage`, pro Browser. Das kann leer
 * zurückkommen oder werfen (privates Fenster, gesperrte Website-Daten),
 * deshalb steht jeder Zugriff in try/catch und im Zweifel wird gezeigt.
 */
(() => {
  const REVISION = '2024-10-15';
  const MERKER_ZU = 'lp_pop_zu';
  const MERKER_OK = 'lp_pop_ok';
  const JAHR = 365 * 24 * 60 * 60 * 1000;

  /** Meldung anzeigen. `art` steuert nur die Farbe. */
  function sagen(el, text, art) {
    el.textContent = text;
    el.dataset.art = art || '';
  }

  /** localStorage, das nie wirft. Fehlt der Wert, kommt 0 zurück. */
  const merker = {
    lesen(name) {
      try {
        return Number(localStorage.getItem(name)) || 0;
      } catch {
        return 0;
      }
    },
    setzen(name) {
      try {
        localStorage.setItem(name, String(Date.now()));
      } catch {
        /* Ohne Merker zeigen wir das Popup wieder. Das ist das kleinere Übel. */
      }
    },
  };

  async function klaviyo(pfad, company, body) {
    const r = await fetch(`https://a.klaviyo.com/client/${pfad}/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
    });
    // Klaviyo antwortet auf Erfolg mit 202 und leerem Rumpf.
    if (!r.ok) throw new Error(`Klaviyo ${pfad}: HTTP ${r.status}`);
  }

  /**
   * Popup scharf machen. Gibt die Funktionen zurück, die das Formular
   * nach dem Absenden braucht.
   */
  function popupAufsetzen(wurzel) {
    // Im Theme-Editor gelten die Sperren nicht, sonst ließe sich das Popup
    // nicht einstellen. Geöffnet wird dort aber nur, wenn die Section
    // angeklickt ist — ein Popup, das bei jeder Bearbeitung aufspringt,
    // wäre im Weg.
    const editor = Boolean(window.Shopify && window.Shopify.designMode);

    if (!editor) {
      // **Nur auf der Produktseite.**
      //
      // Vorher lief es andersherum: das Popup erschien überall, außer wo
      // das Formular ohnehin fest auf der Seite stand — also auf der
      // Startseite, im Blog, auf jeder Inhaltsseite, nur nicht dort, wo
      // gerade ein Buch angesehen wird. Genau verkehrt: Wer auf einer
      // Produktseite steht, hat sich für ein bestimmtes Buch
      // entschieden, und die Leseprobe ist die billigste Art, ihm den
      // ersten Schritt anzubieten. Wer im Blog liest, wollte lesen.
      //
      // Auf der Produktseite steht das Formular weiter unten trotzdem
      // noch einmal fest — das ist Absicht: oben fängt es die ab, die
      // gleich wieder weg wollen, unten die, die bis zum Ende lesen.
      if (!/^\/products\//.test(window.location.pathname)) return null;

      const pauseTage = Number(wurzel.dataset.pause) || 30;
      if (Date.now() - merker.lesen(MERKER_ZU) < pauseTage * 24 * 60 * 60 * 1000) return null;
      if (Date.now() - merker.lesen(MERKER_OK) < JAHR) return null;
    }

    let offen = false;
    let vorher = null;
    // **Einmal weggeklickt heißt weg — für diesen Seitenaufruf.**
    //
    // Der Merker in `localStorage` wird nur hier oben beim Aufsetzen
    // gelesen. Die Auslöser laufen danach weiter: die Verzögerung feuert
    // auch dann noch, wenn das Popup längst geschlossen wurde, und das
    // Verlassen der Seite feuert bei **jeder** Mausbewegung über die
    // obere Kante. Ohne diese Sperre ging das Popup nach dem Kreuz immer
    // wieder auf — gemeldet am 29.10.2026.
    let erledigt = false;
    let beiVerlassen = null;

    function auf() {
      if (offen || erledigt) return;
      // Zweiter Riegel: der Merker, noch einmal zum Zeitpunkt des
      // Öffnens. Er deckt den Fall ab, dass dieselbe Seite das Popup
      // zweimal einhängt — im Theme-Editor passiert genau das, wenn
      // eine Section neu geladen wird.
      const pauseTage = Number(wurzel.dataset.pause) || 30;
      if (Date.now() - merker.lesen(MERKER_ZU) < pauseTage * 24 * 60 * 60 * 1000) return;
      if (Date.now() - merker.lesen(MERKER_OK) < JAHR) return;
      offen = true;
      vorher = document.activeElement;
      wurzel.hidden = false;
      document.body.style.overflow = 'hidden';
      wurzel.querySelector('[data-zu]').focus();
    }

    function zu(merken = true) {
      if (!offen) return;
      offen = false;
      wurzel.hidden = true;
      document.body.style.overflow = '';
      if (merken) {
        merker.setzen(MERKER_ZU);
        erledigt = true;
        // Den Auslöser gleich mit abräumen, nicht nur abfragen: ein
        // Händler, der bei jeder Mausbewegung nach oben läuft und
        // jedes Mal nichts tut, ist überflüssige Arbeit auf einer
        // Seite, die ohnehin schon genug macht.
        if (beiVerlassen) {
          document.removeEventListener('mouseout', beiVerlassen);
          beiVerlassen = null;
        }
      }
      if (vorher && vorher.focus) vorher.focus();
    }

    wurzel.querySelector('[data-zu]').addEventListener('click', () => zu());
    // Nur der Schleier schließt, nicht ein Klick im Kasten.
    wurzel.addEventListener('click', (e) => {
      if (e.target === wurzel) zu();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && offen) zu();
    });

    if (editor) {
      const eigene = (e) => e.detail && e.detail.sectionId === wurzel.dataset.sectionId;
      document.addEventListener('shopify:section:select', (e) => {
        if (eigene(e)) auf();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (eigene(e)) zu(false);
      });
    } else {
      const delay = Number(wurzel.dataset.delay) || 0;
      if (delay > 0) setTimeout(auf, delay);
      if (wurzel.dataset.exit === 'true') {
        // Maus verlässt das Fenster nach oben: Richtung Adresszeile oder
        // Tab-Leiste. Auf dem Handy gibt es das nicht, dort trägt die
        // Verzögerung.
        beiVerlassen = (e) => {
          if (!e.relatedTarget && e.clientY <= 0) auf();
        };
        document.addEventListener('mouseout', beiVerlassen);
      }
    }

    return { zu };
  }

  function start(wurzel) {
    if (wurzel.dataset.lpBereit) return;
    wurzel.dataset.lpBereit = '1';

    const company = wurzel.dataset.company;
    const liste = wurzel.dataset.liste;
    const einwilligungstext = wurzel.dataset.einwilligung || '';
    const wahl = wurzel.querySelector('[data-wahl]');
    const form = wurzel.querySelector('[data-form]');
    const feld = form.querySelector('input[type="email"]');
    const knopf = form.querySelector('button');
    const status = wurzel.querySelector('[data-status]');

    const popup = wurzel.hasAttribute('data-popup') ? popupAufsetzen(wurzel) : null;

    /** Das gerade gewählte Buch, egal ob Auswahl oder verstecktes Feld. */
    const gewaehlt = () => {
      if (wahl.tagName === 'SELECT') return wahl.options[wahl.selectedIndex];
      return wahl;
    };

    // Cover wechselt mit der Auswahl.
    if (wahl.tagName === 'SELECT') {
      wahl.addEventListener('change', () => {
        const id = wahl.value;
        wurzel.querySelectorAll('[data-cover]').forEach((img) => {
          img.classList.toggle('is-aktiv', img.dataset.cover === id);
        });
      });
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = feld.value.trim();
      if (!email || !email.includes('@') || !email.includes('.')) {
        sagen(status, 'Diese Adresse sieht nicht richtig aus.', 'fehler');
        feld.focus();
        return;
      }

      const buch = gewaehlt();
      const titel = buch.dataset.titel || '';
      const pdf = buch.dataset.pdf || '';
      const produkt = buch.dataset.url || '';

      knopf.disabled = true;
      const vorher = knopf.textContent;
      knopf.textContent = 'Moment …';
      sagen(status, '', '');

      try {
        await klaviyo('events', company, {
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: 'Leseprobe angefordert' } } },
              profile: { data: { type: 'profile', attributes: { email } } },
              properties: {
                buch_titel: titel,
                leseprobe_url: pdf,
                buch_url: produkt,
                quelle: window.location.pathname,
              },
            },
          },
        });

        const quelle = titel ? `Leseprobe: ${titel}` : 'Leseprobe';

        // Eigener try-Block: die Leseprobe ist an dieser Stelle schon
        // ausgelöst. Wer sie angefordert hat, soll sie bekommen, auch wenn
        // die Listenanmeldung gerade klemmt.
        if (liste) {
          try {
            // `subscriptions` gehört hier NICHT ins Profil — das lehnt die
            // Client-Schnittstelle mit 400 ab („not a valid field for the
            // resource 'profile'"). Die Einwilligung steckt im Endpunkt
            // selbst, `custom_source` landet als Herkunft am Profil.
            await klaviyo('subscriptions', company, {
              data: {
                type: 'subscription',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        properties: {
                          einwilligung_am: new Date().toISOString(),
                          einwilligung_quelle: window.location.href,
                          einwilligung_buch: titel,
                          einwilligung_wortlaut: einwilligungstext,
                        },
                      },
                    },
                  },
                  custom_source: quelle,
                },
                relationships: { list: { data: { type: 'list', id: liste } } },
              },
            });
          } catch (err) {
            console.error('[Leseprobe] Anmeldung fehlgeschlagen', err);
          }
        }

        form.hidden = true;
        const satz = wurzel.querySelector('.lp__einwilligung');
        if (satz) satz.hidden = true;
        sagen(
          status,
          'Unterwegs. Sieh gleich in deinem Postfach nach.',
          'gut'
        );
        // Wer die Leseprobe hat, braucht das Popup nicht wieder.
        if (popup) merker.setzen(MERKER_OK);
      } catch (err) {
        // Nichts verschlucken: wer keine Mail bekommt, soll wissen warum.
        console.error('[Leseprobe]', err);
        sagen(status, 'Das hat gerade nicht geklappt. Versuch es bitte nochmal.', 'fehler');
        knopf.disabled = false;
        knopf.textContent = vorher;
      }
    });
  }

  const alle = () => document.querySelectorAll('[data-lp]').forEach(start);
  if (document.readyState !== 'loading') alle();
  else document.addEventListener('DOMContentLoaded', alle);
  // Horizon tauscht Sections im Editor aus, ohne die Seite neu zu laden.
  document.addEventListener('shopify:section:load', alle);
})();
